'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { authClient } from '@/lib/auth/auth-client'
import { useAuthStore } from '@/lib/stores/auth'
import type { User as DBUser } from '@/lib/types/database'
import { isClientPublicPath } from '@/lib/providers/auth-provider-public-paths'
import { startDeferredSessionCheck } from '@/lib/providers/auth-provider-deferred-session-check'
import {
  buildAfterLoginHref,
  buildLoginHrefFromHere,
  nextPathFromSearch,
} from '@/lib/utils/safe-next-path'
// 型のみの import（コンパイル時に消えるのでサーバー専用コードは client bundle に入らない）。
// 定義を1か所にすることで app/layout.tsx が渡す形とここで受ける形がズレなくなる。
import type { ServerUser } from '@/lib/auth/server-user'

function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(id))
}

interface AuthProviderProps {
  children: React.ReactNode
  serverUser?: ServerUser | null
}

// 認証不要のパス判定は lib/providers/auth-provider-public-paths.ts に一本化する。
// middleware の正本 (ALL_PUBLIC_PAGE_PATHS) を必ず包含するため、ここに配列を復活させない。

export function AuthProvider({ children, serverUser }: AuthProviderProps) {
  const router = useRouter()
  const pathname = usePathname()
  const {
    isAuthenticated,
    isLoading,
    sessionCheckErrored,
    setUser,
    setLoading,
    setSessionCheckErrored,
    setImpersonateStatus,
  } = useAuthStore()
  const initDone = useRef(false)
  /** role='sales' の再取得タイマー。アンマウント時に止める。 */
  const roleRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 🔴 ICD-99（2026-09-29）: 公開の画面で始まり、セッションをまだ確かめていない印と、その確認。
  //    初期化は1回だけ（initDone）・公開の画面では確認を飛ばす・ログイン状態は残さない
  //    （lib/stores/auth.ts の partialize）ため、印が無いと非公開の画面へ移ったとき
  //    「確かめていない」を「ログインしていない」と取り違えて /login へ送っていた。
  const sessionCheckDeferredRef = useRef(false)
  const runDeferredSessionCheckRef = useRef<(() => void) | null>(null)
  // 確認の最中は送らない印と、終わった回数（増えるとリダイレクトの effect が結果を見直す）。
  // 共有の isLoading だけだと、途中で他所が解いたときに結果を待たずに送る（Codex SO MEDIUM）。
  const deferredCheckInFlightRef = useRef(false)
  const [deferredCheckSettledCount, setDeferredCheckSettledCount] = useState(0)
  // 画面に付いている間だけ true（アンマウント後に setUser しない印）。初期化の effect の中の変数で
  // 持つと、StrictMode の付け外しで true に戻らず後の確認で role の再取得が止まる（Codex SO MEDIUM）。
  const mountedRef = useRef(false)

  // サーバーが提供した正しいroleでlocalStorageの古いデータを初回レンダリング前に修正
  // /api/users呼び出しが失敗し続ける場合でも role='sales' が表示されることを防ぐ
  useLayoutEffect(() => {
    if (!serverUser?.email || !serverUser.role) return
    const currentUser = useAuthStore.getState().user
    if (currentUser?.email === serverUser.email && currentUser.role !== serverUser.role) {
      setUser({
        ...currentUser,
        role: serverUser.role,
        availableRoles: serverUser.availableRoles ?? currentUser.availableRoles,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- SSR時のみ実行
  }, [])

  // 初期認証状態の確認（1回のみ実行）
  useEffect(() => {
    if (initDone.current) return
    initDone.current = true
    // アンマウント後に state を書かないための印は `mountedRef`（初期化は fetch を何度か挟むので、
    // 画面を離れた後に戻ってくることがある）。

    // 公開パスではセッションチェックをスキップ。
    // ただし /login だけは特別扱い: stale localStorage の認証状態を放置すると
    // 次回ページ遷移時に「ゴーストログイン状態」で 401 が出る事故を引き起こす（Codex 指摘）。
    // /login では getSession() で session 確認 → 有効なら /bt-progress へ、無効なら state クリア。
    const isLoginPath = pathname === '/login' || pathname.startsWith('/login/')
    const isPublicPath = isClientPublicPath(pathname)

    if (isLoginPath) {
      ;(async () => {
        try {
          const { data: session } = await authClient.getSession()
          if (session?.user) {
            // 有効 session が残っていれば最初の画面（/ ＝ 設計・IC は ArchiDeck・それ以外は進捗管理表）へ。
            //
            // 2026-09-13 実測: ここが router.replace（画面内遷移）だと、この初期化は
            // 1 回しか走らないため isLoading=true が遷移先へ持ち越され、Layout の
            // LoadingShield が永久に残る（再読み込みするまで「読み込み中」から動かない）。
            // ログインフォーム成功時（use-login-form.ts の window.location.href）と同じく
            // ページ全体を読み直し、遷移先で認証初期化を最初から走らせる。
            // 機構: lib/providers/__tests__/auth-provider-login-redirect.test.tsx
            // AUTH-01（2026-10-30）: ログインの直後と同じ行き先を決める口を通す（2段階認証が必須の人は登録の画面へ・ほかの人は最初の画面 "/"＝ADK-71）
            // 戻り先（?next=）は落とさない（2026-11-01 入力フォームの URL から来た人が最初の画面へ行った）
            window.location.replace(buildAfterLoginHref(nextPathFromSearch(window.location.search)))
            return
          }
        } catch (_e) {
          // ネットワークエラー時は state クリアにフォールバック（意図的に握り潰し）
        }
        // 無効 / 取得不能: stale auth state をクリア
        setUser(null)
      })()
      return
    }

    const authenticateUser = async (sessionUser: {
      id: string
      email?: string | null
      name?: string | null
    }) => {
      // なりすまし中の場合、ターゲットユーザーのプロフィールを使用
      let targetEmail = sessionUser.email || ''
      let isImpersonating = false
      try {
        const impRes = await fetchWithTimeout('/api/auth/impersonate/status', 8_000)
        if (impRes.ok) {
          const impData = await impRes.json()
          if (impData.isImpersonating && impData.targetEmail) {
            targetEmail = impData.targetEmail
            isImpersonating = true
          }
          // なりすまし状態をstoreに保存（ImpersonateBannerが再度APIを呼ばなくて済む）
          setImpersonateStatus(impData.isImpersonating ?? false, impData.targetName ?? null)
        }
      } catch (_) {
        /* ignore */
      }

      // なりすまし解除後のlocalStorage不整合を検知・修正
      // cookieが消えている（なりすましでない）のに、localStorageに別ユーザーのデータが残っている場合はクリア
      if (!isImpersonating) {
        try {
          const stored = JSON.parse(localStorage.getItem('ghouse-auth') || '{}')
          const storedEmail = stored?.state?.user?.email
          if (storedEmail && sessionUser.email && storedEmail !== sessionUser.email) {
            localStorage.removeItem('ghouse-auth')
          }
        } catch (_) {
          /* ignore */
        }
      }

      // /api/users が一時的に失敗する場合（コールドスタート等）に備えてリトライする
      const fetchDbUser = async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const res = await fetchWithTimeout(
              `/api/users?email=${encodeURIComponent(targetEmail)}`,
              8_000
            )
            if (res.ok) {
              const json = await res.json()
              return json.data ?? null
            }
          } catch (_) {
            /* ignore */
          }
          if (attempt < 2) await new Promise((r) => setTimeout(r, 600 * (attempt + 1)))
        }
        return null
      }

      // 【自分の情報を取る第2の口】/api/users は withPolicy({ resourceType: 'user',
      // action: 'view' }) で守られており、顧客ポータルロール (role='user') は evaluator の
      // 社員判定 (`user.role && user.role !== 'user'`) を通れず default deny になる。
      // ＝お客様は /api/users から**自分の情報すら**取得できない。
      // /api/auth/profile はセッションのみで通り「セッション本人の email の分だけ」返す
      // （他人の email を渡すと 403）ため、お客様でも本物の role / tenant_id が取れる。
      // なりすまし中に targetEmail（相手）を渡した場合は 403 → null になるだけで、
      // なりすまし元の情報が漏れることはない（ADR-031: 判定は相手のロールだけを使う）。
      const fetchOwnProfile = async (): Promise<DBUser | null> => {
        try {
          const res = await fetchWithTimeout(
            `/api/auth/profile?email=${encodeURIComponent(targetEmail)}`,
            8_000
          )
          if (!res.ok) return null
          const p = await res.json()
          if (!p?.id) return null
          return {
            id: p.id,
            tenantId: p.tenant_id,
            email: p.email,
            name: p.name,
            phone: p.phone ?? null,
            // 権限は補完しない（app/api/auth/profile/route.ts:60 と同じ方針・2026-07-22）。
            role: p.role ?? null,
            availableRoles: null,
            image: null,
            emailVerified: null,
            isActive: p.is_active ?? true,
            departmentId: p.departmentId ?? null,
            teamId: p.teamId ?? null,
            memberId: p.memberId ?? null,
            franchiseId: null,
            mustChangePassword: p.mustChangePassword ?? false,
            twoFactorEnabled: false,
            createdAt: p.created_at ? new Date(p.created_at) : new Date(),
            updatedAt: new Date(),
            lastActiveAt: null,
            // User 型が要る列なので DB の既定（default false・NULL 可）で埋めるだけ。停止の判定には使わない（停止は isActive の 1 か所）
            banned: false,
            banReason: null,
            banExpires: null,
            displayTemplate: null,
          }
        } catch (_) {
          return null
        }
      }

      const dbUser = (await fetchDbUser()) ?? (await fetchOwnProfile())

      if (dbUser) {
        setUser(dbUser)
      } else {
        // どちらの口からも取得できず: storeに既存ユーザーデータがあればそのまま保持する。
        // role='sales' のフォールバックで上書きすると localStorage に誤ったroleが
        // 永続化され、ページ遷移後も「営業」表示が残り続けるバグの原因になる。
        const existingUser = useAuthStore.getState().user
        // localStorageに旧版のsalesフォールバックが残っている場合、バックグラウンドで再試行
        // 🔴 タイマーは cleanup で止める（台帳 USEEFFECT-CLEANUP）。5秒後に走るうえ中で 2回 fetch
        // するので、画面を離れた後に setUser が走りうる。
        // ここで mountedRef も見る: 初期化は内部のリトライで十数秒かかることがあり、**アンマウント後に
        // この行へ到達しうる**。そのとき仕掛けても、止める人（cleanup）はもういない。
        if (existingUser?.role === 'sales' && mountedRef.current) {
          roleRetryTimerRef.current = setTimeout(async () => {
            const retryUser = (await fetchDbUser()) ?? (await fetchOwnProfile())
            if (retryUser && mountedRef.current) setUser(retryUser)
          }, 5000)
        }
        if (!existingUser) {
          // setUser を呼ばないと isLoading が解除されず画面が永久ローディングになるため、
          // 最低限のユーザーは作る。ただし **role は絶対に補完しない**（null のまま）。
          // getRoleDisplay(null) は空文字を返すので「営業」は表示されない。
          // tenantId は本物が取れないため現行と同じ単一テナント値を維持する
          // （DEFAULT_TENANT_ID の新規 import は eslint-plugin-gforce の
          //   no-new-default-tenant-id が禁止。本物の tenant_id は上の
          //   /api/auth/profile 経路で入るので、ここへ来るのは両方失敗した時だけ）。
          const newUser: DBUser = {
            id: sessionUser.id,
            tenantId: '00000000-0000-0000-0000-000000000001',
            email: sessionUser.email || '',
            name: sessionUser.name || sessionUser.email?.split('@')[0] || 'User',
            phone: null,
            role: null,
            availableRoles: null,
            image: null,
            emailVerified: null,
            isActive: true,
            departmentId: null,
            teamId: null,
            memberId: null,
            franchiseId: null,
            mustChangePassword: false,
            twoFactorEnabled: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastActiveAt: null,
            // User 型が要る列なので DB の既定（default false・NULL 可）で埋めるだけ。停止の判定には使わない（停止は isActive の 1 か所）
            banned: false,
            banReason: null,
            banExpires: null,
            displayTemplate: null,
          }
          setUser(newUser)
        }
      }
    }

    const getSessionWithTimeout = () =>
      Promise.race([
        authClient.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession timeout')), 13_000)
        ),
      ])

    const initAuth = async () => {
      // 【高速パス】localStorage にキャッシュユーザーがあれば isLoading を即解除してページを表示する。
      // session 検証はバックグラウンドで継続し、結果に応じてユーザー情報を更新 or ログアウト。
      // これにより Vercel コールドスタート等による長時間ローディング（最悪60秒超）を解消する。
      const { user: storedUser } = useAuthStore.getState()
      if (storedUser) {
        setUser(storedUser) // isLoading=false → ページが即座に表示される
      }

      try {
        // better-auth セッションを確認（13秒でタイムアウト）
        const { data: session, error } = await getSessionWithTimeout()

        if (session?.user) {
          // セッション有効: 最新ユーザーデータに更新（バックグラウンドで実行）
          await authenticateUser(session.user)
          return
        }

        // サーバーエラー（500, DB接続失敗, コールドスタート等）の場合は強制ログアウトしない。
        // storedUser が無い（cold navigation 等）場合、単に isLoading を解除するだけだと
        // isAuthenticated=false のまま「未認証なら/loginへ送る」effectが発火し、middleware が
        // 既に確認した有効な cookie を無視して誤送還してしまう（GF-D-08・2026-08-29事故）。
        // 「確認できて未ログインだった」と区別するため sessionCheckErrored を立てる。
        if (error) {
          if (!storedUser) {
            setSessionCheckErrored(true)
            setLoading(false)
          }
          return
        }

        // セッション未検出（正常な200レスポンスでdataがnull）
        // Vercelコールドスタートやネットワーク瞬断の可能性があるためリトライ
        if (storedUser) {
          await new Promise((resolve) => setTimeout(resolve, 800))
          const { data: retrySession, error: retryError } = await getSessionWithTimeout()
          if (retrySession?.user) {
            await authenticateUser(retrySession.user)
            return
          }
          // リトライもサーバーエラーの場合は強制ログアウトしない（キャッシュで継続）
          if (retryError) return
        }

        // セッションが本当にない場合は認証状態をクリア
        setUser(null)
      } catch (_) {
        // ネットワークエラー・タイムアウト等の一時的な障害では強制ログアウトしない。
        // GF-D-08: storedUser が無い場合、単に isLoading を解除するだけでは
        // 「未認証なら/loginへ送る」effectが誤発火する（上の error 分岐と同じ理由）。
        if (!storedUser) {
          setSessionCheckErrored(true)
          setLoading(false)
        }
      }
    }

    if (isPublicPath) {
      // 公開の画面ではここでは確かめない（お客様の画面・LINE 等に通信を足さない）。
      // 非公開の画面へ画面の中で移ったときに、/login へ送る前に確かめる（下のリダイレクトの effect・ICD-99）。
      sessionCheckDeferredRef.current = true
      runDeferredSessionCheckRef.current = () => {
        void startDeferredSessionCheck(initAuth, {
          mountedRef,
          inFlightRef: deferredCheckInFlightRef,
          onSettled: () => setDeferredCheckSettledCount((n) => n + 1),
        })
      }
      setLoading(false)
    } else {
      initAuth()
    }
    // 後始末（再取得タイマーを止める・アンマウントの印）は下の mountedRef の effect が持つ
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ認証初期化したい。pathname/setImpersonateStatus の変更追従は不要
  }, [router, setUser, setLoading])

  // 付け外しのたびに入れ直す（StrictMode の setup → cleanup → setup でも最後は true に戻る）
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (roleRetryTimerRef.current) {
        clearTimeout(roleRetryTimerRef.current)
        roleRetryTimerRef.current = null
      }
    }
  }, [])

  // 未認証ユーザーをログインページにリダイレクト
  useEffect(() => {
    if (isLoading) return // ロード中はリダイレクトしない
    // GF-D-08: セッション確認が timeout/network error/500 等で「未確認」に終わった場合は
    // 送らない。middleware が edge で確認済みの session cookie（＝このページが描画されている
    // 前提条件）を、クライアント側の再確認失敗だけで覆さない（2026-08-29全社ログイン停止事故）。
    if (sessionCheckErrored) return
    // 🔴 ICD-99: 後回しにした確認の最中は送らない。終わると deferredCheckSettledCount が増え、
    //    ここがもう一度走って結果（ログインあり／なし／確認できず）を見る。
    if (deferredCheckInFlightRef.current) return

    const isPublicPath = isClientPublicPath(pathname)

    if (!isAuthenticated && !isPublicPath) {
      // 🔴 ICD-99: 公開の画面から入ってまだ確かめていないなら、送る前に1回だけ確かめる。
      //    確かめている間は読み込み中にし、結果（ログインあり／なし／確認できず）はこの effect が
      //    もう一度受け取る。ログインが無いと確かめたときだけ /login へ送る（GF-D-08 はそのまま効く）。
      const runDeferredSessionCheck = runDeferredSessionCheckRef.current
      if (sessionCheckDeferredRef.current && runDeferredSessionCheck) {
        sessionCheckDeferredRef.current = false
        setLoading(true)
        runDeferredSessionCheck()
        return
      }
      router.push(buildLoginHrefFromHere())
    }
  }, [
    isAuthenticated,
    isLoading,
    sessionCheckErrored,
    pathname,
    router,
    setLoading,
    deferredCheckSettledCount,
  ])

  return <>{children}</>
}
